import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

/**
 * PhilosophyGate — Écran transition "Règle 80/20 de Sabril"
 * Affiché une fois entre la fin du chargement et l'affichage du diagnostic.
 * Plante la philosophie Sabril dans la tête du lead AVANT qu'il voie ses chiffres.
 */
const PhilosophyGate = ({ onDismiss, restoName }) => {
  const [animateDonut, setAnimateDonut] = useState(false);

  // Trigger donut animation on mount
  useEffect(() => {
    const t = setTimeout(() => setAnimateDonut(true), 350);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6 overflow-y-auto py-8"
      style={{ background: 'linear-gradient(180deg, #0f0606 0%, #1f0d0d 100%)' }}
      data-testid="philosophy-gate"
    >
      {/* Background radial gradient */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 30% 30%, rgba(240,90,26,0.20) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(16,185,129,0.15) 0%, transparent 50%)',
        }}
        aria-hidden="true"
      />

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="relative max-w-2xl w-full"
      >
        {/* Sabril header */}
        <div className="flex items-center gap-3 mb-6 justify-center sm:justify-start">
          <div className="relative w-12 h-12 flex-shrink-0">
            <span className="absolute inset-0 rounded-full bg-[#F05A1A]/40 blur-md animate-pulse" />
            <img
              src="/sabril-avatar.png"
              alt="Sabril"
              className="relative w-12 h-12 rounded-full object-cover border-2 border-white shadow-lg bg-white"
              draggable={false}
            />
            <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-[#F05A1A] leading-none">
              Sabril · en ligne
            </p>
            <p className="text-white/70 text-xs mt-1">
              Avant de lire votre audit, retiens ça
            </p>
          </div>
        </div>

        {/* Title */}
        <h2 className="font-bebas text-3xl sm:text-5xl text-white text-center sm:text-left leading-[0.95] mb-6">
          La <span className="text-[#F05A1A]">règle 80/20</span><br/>
          du restaurateur qui dure
        </h2>

        {/* Main block — Donut + Insight */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-6 sm:gap-8 items-center bg-white/[0.04] rounded-3xl p-5 sm:p-8 border border-white/10 mb-6">
          {/* Donut chart SVG (80/20) */}
          <div className="sm:col-span-2 flex justify-center">
            <div className="relative w-44 h-44 sm:w-52 sm:h-52">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                {/* Background circle (20% — opérationnel rouge) */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="14"
                  strokeOpacity="0.85"
                />
                {/* Foreground arc (80% — structure & développement vert) */}
                <motion.circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="14"
                  strokeLinecap="round"
                  strokeDasharray="251.327"
                  initial={{ strokeDashoffset: 251.327 }}
                  animate={{ strokeDashoffset: animateDonut ? 251.327 * 0.2 : 251.327 }}
                  transition={{ duration: 1.4, ease: 'easeOut' }}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(16,185,129,0.4))' }}
                />
              </svg>
              {/* Center label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="font-bebas text-5xl sm:text-6xl text-white leading-none tabular-nums">80<span className="text-xl text-white/70">%</span></p>
                <p className="text-green-400 text-[10px] font-black uppercase tracking-widest mt-1">Structure</p>
              </div>
            </div>
          </div>

          {/* Insight breakdown */}
          <div className="sm:col-span-3 space-y-3">
            {/* 80% — Structure */}
            <div className="rounded-xl p-4 border-2 border-green-500/40 bg-green-500/5">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                <p className="text-[10px] font-black uppercase tracking-widest text-green-400">
                  80% — Là où vous construisez
                </p>
              </div>
              <p className="font-bebas text-xl sm:text-2xl text-white leading-tight">
                Structure & développement
              </p>
              <p className="text-white/65 text-[11px] sm:text-xs leading-snug mt-1">
                Pilotage chiffré · process · recrutement stratégique ·
                marque · marketing · scale · vision
              </p>
            </div>

            {/* 20% — Opérationnel */}
            <div className="rounded-xl p-4 border-2 border-red-500/30 bg-red-500/5">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex w-2 h-2 rounded-full bg-red-500" />
                <p className="text-[10px] font-black uppercase tracking-widest text-red-400">
                  20% — Là où vous maintenez
                </p>
              </div>
              <p className="font-bebas text-xl sm:text-2xl text-white/80 leading-tight">
                Opérationnel quotidien
              </p>
              <p className="text-white/55 text-[11px] sm:text-xs leading-snug mt-1">
                Service · feu · planning urgent · imprévus —
                tout ce qui DOIT être délégué à terme
              </p>
            </div>
          </div>
        </div>

        {/* Warning + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="rounded-2xl p-5 mb-5 bg-gradient-to-br from-[#F05A1A]/10 to-red-500/5 border-2 border-[#F05A1A]/40"
        >
          <p className="text-white text-sm sm:text-[15px] leading-relaxed">
            <span className="font-black text-[#F05A1A]">⚠️ Si vous faites l'inverse</span> — 80% d'opérationnel,
            20% de structure — <span className="font-bold text-white">vous subissez votre resto au lieu de le piloter.</span>{' '}
            <span className="text-white/75">Peu importe votre CA. Peu importe votre talent en cuisine. Le plafond arrive vite.</span>
          </p>
          <p className="text-white/80 text-sm sm:text-[15px] leading-relaxed mt-3">
            <span className="font-bold text-white">{restoName ? restoName : 'Gardez ce ratio en tête'}</span>{' '}
            quand vous allez lire votre audit — chaque ligne va vous dire si vous êtes du bon côté de cette équation.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.button
          onClick={onDismiss}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-gradient-to-r from-[#F05A1A] to-[#FF7A3D] hover:from-[#FF7A3D] hover:to-[#F05A1A] text-white font-black uppercase tracking-wider text-sm sm:text-base py-4 rounded-2xl shadow-[0_12px_30px_rgba(240,90,26,0.4)] transition-all flex items-center justify-center gap-3"
          data-testid="philosophy-gate-cta"
        >
          <span>Compris · Voir mon diagnostic</span>
          <span className="text-lg">→</span>
        </motion.button>

        {/* Skip link (subtil) */}
        <button
          onClick={onDismiss}
          className="block mx-auto mt-3 text-white/35 hover:text-white/60 text-[11px] underline transition-colors"
          data-testid="philosophy-gate-skip"
        >
          Passer
        </button>
      </motion.div>
    </motion.div>
  );
};

export default PhilosophyGate;
